import { Presentation, BookOpen, FileText, Video, MessagesSquare, Award } from "lucide-react";
import { Reveal } from "./Reveal";
import { BuyButton } from "./BuyButton";
import { IMAGES } from "./constants";

const card =
  "group rounded-3xl border border-slate-200 bg-white p-8 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-terra/50 lg:p-10";

export const Toolkit = () => (
  <section id="inside" className="py-24 lg:py-32" data-testid="toolkit-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal className="max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-terra">What's inside the toolkit</p>
        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
          Everything you need to facilitate with confidence — even if you've never trained a room.
        </h2>
        <p className="mt-6 text-base leading-relaxed text-navy/70 md:text-lg">
          A complete facilitation package for a two-hour Building a Speak-Up Culture™ session:
          structured activities, discussion prompts, facilitation tips, and language you can adapt
          to fit any culture. Download, customize, add your logo, and make it yours.
        </p>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-12">
        <Reveal className="md:col-span-7">
          <div className={`${card} h-full`} data-testid="toolkit-card-slides">
            <div className="mb-8 overflow-hidden rounded-2xl">
              <img src={IMAGES.slides} alt="Training slides lesson inside the Building a Speak-Up Culture toolkit portal" className="h-56 w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" loading="lazy" />
            </div>
            <Presentation className="h-8 w-8 text-terra" aria-hidden="true" />
            <h3 className="mt-4 font-display text-2xl font-bold tracking-tight">Two-Hour Training Slides</h3>
            <p className="mt-3 leading-relaxed text-navy/70">
              The full, professionally designed deck for a complete upstander and bystander
              intervention session — research-backed, discussion-driven, and ready to present
              under your brand.
            </p>
          </div>
        </Reveal>

        <Reveal className="md:col-span-5" delay={0.08}>
          <div className={`${card} h-full`} data-testid="toolkit-card-guide">
            <div className="mb-8 overflow-hidden rounded-2xl">
              <img src="/toolkit/welcome-packet.jpg" alt="Welcome packet with a message from the CEO inside the toolkit portal" className="h-48 w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" loading="lazy" />
            </div>
            <BookOpen className="h-8 w-8 text-terra" aria-hidden="true" />
            <h3 className="mt-4 font-display text-2xl font-bold tracking-tight">Facilitator's Guide</h3>
            <p className="mt-3 leading-relaxed text-navy/70">
              A welcome packet and upfront summary with step-by-step delivery guidance — how to
              open the session, create psychological safety, and handle tough learner moments.
            </p>
          </div>
        </Reveal>

        <Reveal className="md:col-span-5" delay={0.05}>
          <div className={`${card} h-full`} data-testid="toolkit-card-workbook">
            <FileText className="h-8 w-8 text-terra" aria-hidden="true" />
            <h3 className="mt-4 font-display text-2xl font-bold tracking-tight">Participant Workbook &amp; Handouts</h3>
            <p className="mt-3 leading-relaxed text-navy/70">
              A learner notebook packed with activities, reflection prompts, and take-away tools
              that keep the conversation going long after the session ends.
            </p>
          </div>
        </Reveal>

        <Reveal className="md:col-span-7" delay={0.1}>
          <div className={`${card} h-full`} data-testid="toolkit-card-videos">
            <Video className="h-8 w-8 text-terra" aria-hidden="true" />
            <h3 className="mt-4 font-display text-2xl font-bold tracking-tight">Tutorial Videos from Expert Facilitators</h3>
            <p className="mt-3 leading-relaxed text-navy/70">
              Watch Catherine Mattice in conversation with Civility Partners facilitators
              Dr. Toni Herndon and Dr. Bob Berk — honest, practical guidance on delivering the
              training well, responding to resistance, and acknowledging the fear of speaking up.
            </p>
          </div>
        </Reveal>

        <Reveal className="md:col-span-6" delay={0.05}>
          <div className={`${card} h-full`} data-testid="toolkit-card-faq-video">
            <MessagesSquare className="h-8 w-8 text-terra" aria-hidden="true" />
            <h3 className="mt-4 font-display text-2xl font-bold tracking-tight">FAQ Video + Office Hours Recording</h3>
            <p className="mt-3 leading-relaxed text-navy/70">
              Answers to the questions facilitators actually ask, plus a recorded office-hours
              session on how to use and customize the toolkit.
            </p>
          </div>
        </Reveal>

        <Reveal className="md:col-span-6" delay={0.1}>
          <div className={`${card} h-full`} data-testid="toolkit-card-certificate">
            <Award className="h-8 w-8 text-terra" aria-hidden="true" />
            <h3 className="mt-4 font-display text-2xl font-bold tracking-tight">Certificate of Completion</h3>
            <p className="mt-3 leading-relaxed text-navy/70">
              Finish the train-the-trainer course and earn a certificate — proof you're equipped
              to lead speak-up culture training with credibility.
            </p>
          </div>
        </Reveal>
      </div>

      <Reveal className="mt-14">
        <BuyButton testId="toolkit-buy-button" size="lg">Get Instant Access — $249</BuyButton>
      </Reveal>
    </div>
  </section>
);
